// MergeDuplicatesModal — finds & merges duplicate CRM contacts.
// Pulls candidate groups from /api/crm/duplicates/candidates, lets the user
// pick a target per group + which sources to fold in, and calls /api/crm/merge.
import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { X as XIcon, RefreshCw, GitMerge, Crown, CheckCircle2, AlertCircle, Search } from "lucide-react";

export default function MergeDuplicatesModal({ onClose, onMerged }) {
  const [tab, setTab] = useState("suggested"); // suggested | manual
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [merging, setMerging] = useState(null); // group index currently merging
  // Per group: { targetId, checkedSources: Set<id> }
  const [selections, setSelections] = useState({});

  // Manual merge state
  const [allContacts, setAllContacts] = useState([]);
  const [manualLoading, setManualLoading] = useState(false);
  const [manualSearch, setManualSearch] = useState("");
  const [manualChecked, setManualChecked] = useState(new Set()); // Set<contact.id>
  const [manualTargetId, setManualTargetId] = useState(null);
  const [manualMerging, setManualMerging] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/api/crm/duplicates/candidates");
      const gs = r.data?.groups || [];
      setGroups(gs);
      // Default selections: suggested target + all other members as sources
      const sel = {};
      gs.forEach((g, i) => {
        sel[i] = {
          targetId: g.suggested_target_id,
          checkedSources: new Set(g.members.filter(m => m.id !== g.suggested_target_id).map(m => m.id)),
        };
      });
      setSelections(sel);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load duplicate candidates");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const loadAllContacts = async () => {
    setManualLoading(true);
    try {
      const r = await api.get("/api/crm/");
      setAllContacts(r.data || []);
    } catch { toast.error("Could not load contacts"); }
    finally { setManualLoading(false); }
  };

  useEffect(() => {
    if (tab === "manual" && allContacts.length === 0) loadAllContacts();
  }, [tab, allContacts.length]);

  const filteredContacts = useMemo(() => {
    const term = manualSearch.trim().toLowerCase();
    if (!term) return allContacts.slice(0, 300);
    return allContacts.filter(c => (
      (c.stage_name || "").toLowerCase().includes(term) ||
      (c.legal_name || "").toLowerCase().includes(term) ||
      (c.email || "").toLowerCase().includes(term) ||
      (c.twitter || "").toLowerCase().includes(term) ||
      (c.instagram || "").toLowerCase().includes(term) ||
      (c.city || "").toLowerCase().includes(term)
    )).slice(0, 300);
  }, [allContacts, manualSearch]);

  const toggleManualCheck = (id) => {
    setManualChecked(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
        if (manualTargetId === id) setManualTargetId(null);
      } else {
        next.add(id);
        if (!manualTargetId) setManualTargetId(id);
      }
      return next;
    });
  };

  const runManualMerge = async () => {
    if (!manualTargetId) return toast.error("Pick a target first");
    const sources = Array.from(manualChecked).filter(id => id !== manualTargetId);
    if (sources.length === 0) return toast.error("Check at least 1 source (+ 1 target)");
    const target = allContacts.find(c => c.id === manualTargetId);
    const targetName = target?.stage_name || target?.legal_name || "target";
    if (!window.confirm(
      `Merge ${sources.length} contact${sources.length === 1 ? "" : "s"} into "${targetName}"?\n\n` +
      `This will DELETE the sources and rewrite all their references (videos, pictures, posts, submissions).`
    )) return;
    setManualMerging(true);
    try {
      const r = await api.post("/api/crm/merge", { target_id: manualTargetId, source_ids: sources });
      toast.success(`✓ Merged ${r.data.deleted_sources} into "${targetName}"`);
      setManualChecked(new Set());
      setManualTargetId(null);
      // Refresh both lists
      setAllContacts(prev => prev.filter(c => !sources.includes(c.id)));
      onMerged && onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setManualMerging(false); }
  };

  const setTarget = (groupIdx, newTargetId) => {
    setSelections(prev => {
      const cur = prev[groupIdx] || { targetId: null, checkedSources: new Set() };
      const nextSources = new Set(groups[groupIdx].members.filter(m => m.id !== newTargetId).map(m => m.id));
      // Preserve prior unchecks if user manually unchecked any
      cur.checkedSources.forEach(id => { if (!nextSources.has(id)) nextSources.add(id); });
      return { ...prev, [groupIdx]: { targetId: newTargetId, checkedSources: nextSources } };
    });
  };

  const toggleSource = (groupIdx, id) => {
    setSelections(prev => {
      const cur = prev[groupIdx];
      const nextSet = new Set(cur.checkedSources);
      if (nextSet.has(id)) nextSet.delete(id); else nextSet.add(id);
      return { ...prev, [groupIdx]: { ...cur, checkedSources: nextSet } };
    });
  };

  const mergeGroup = async (groupIdx) => {
    const sel = selections[groupIdx];
    if (!sel?.targetId) return toast.error("Pick a target first");
    const sourceIds = Array.from(sel.checkedSources).filter(id => id !== sel.targetId);
    if (sourceIds.length === 0) return toast.error("Check at least one source to merge in");
    if (!window.confirm(`Merge ${sourceIds.length} contact${sourceIds.length === 1 ? "" : "s"} into the target?\n\nThis will delete the sources and rewrite all their references (videos, pictures, posts, submissions).`)) return;
    setMerging(groupIdx);
    try {
      const r = await api.post("/api/crm/merge", { target_id: sel.targetId, source_ids: sourceIds });
      toast.success(`✓ Merged ${r.data.deleted_sources} contact${r.data.deleted_sources === 1 ? "" : "s"} into target`);
      // Remove group from list optimistically
      setGroups(gs => gs.filter((_, i) => i !== groupIdx));
      setSelections(prev => {
        const next = { ...prev };
        delete next[groupIdx];
        return next;
      });
      onMerged && onMerged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Merge failed");
    } finally { setMerging(null); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="crm-merge-modal"
      style={{ background: "rgba(0,0,0,0.8)", backdropFilter: "blur(6px)" }} onClick={onClose}>
      <div className="bg-zinc-950 rounded-2xl w-full max-w-5xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()} style={{ border: "1px solid var(--gio-border)" }}>
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: "var(--gio-border)" }}>
          <div>
            <h3 className="font-heading text-lg inline-flex items-center gap-2">
              <GitMerge size={18} style={{ color: "var(--gio-primary)" }} /> Merge duplicate CRM contacts
            </h3>
            <div className="text-[11px] mt-0.5" style={{ color: "var(--gio-muted-2)" }}>
              {tab === "suggested"
                ? "Matches: same email · phone · Twitter/IG/OF handle · fuzzy name similarity ≥ 85%."
                : "Manually pick any contacts to merge — search, check the ones you want, mark one as target, then merge."}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {tab === "suggested" && (
              <button onClick={load} disabled={loading} data-testid="merge-refresh"
                className="btn-ghost text-[11px] inline-flex items-center gap-1.5">
                <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Rescan
              </button>
            )}
            <button onClick={onClose} data-testid="merge-close" className="text-zinc-400 hover:text-red-400">
              <XIcon size={18} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b" style={{ borderColor: "var(--gio-border)" }} data-testid="merge-tabs">
          <button onClick={() => setTab("suggested")} data-testid="merge-tab-suggested"
            className="px-5 py-3 text-[12px] font-bold uppercase tracking-wider border-b-2 transition"
            style={{
              color: tab === "suggested" ? "var(--gio-primary)" : "var(--gio-muted-2)",
              borderColor: tab === "suggested" ? "var(--gio-primary)" : "transparent",
            }}>
            🤖 Suggested{groups.length > 0 ? ` (${groups.length})` : ""}
          </button>
          <button onClick={() => setTab("manual")} data-testid="merge-tab-manual"
            className="px-5 py-3 text-[12px] font-bold uppercase tracking-wider border-b-2 transition"
            style={{
              color: tab === "manual" ? "var(--gio-primary)" : "var(--gio-muted-2)",
              borderColor: tab === "manual" ? "var(--gio-primary)" : "transparent",
            }}>
            🎯 Manual pick
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {tab === "suggested" && (
            <>
              {loading && (
            <div className="text-center py-16 text-[13px]" style={{ color: "var(--gio-muted-3)" }}>
              Scanning your CRM for duplicates…
            </div>
          )}
          {!loading && groups.length === 0 && (
            <div className="text-center py-16" data-testid="merge-empty">
              <CheckCircle2 size={40} className="mx-auto mb-3" style={{ color: "var(--gio-secondary)" }} />
              <div className="font-heading text-[16px]" style={{ color: "var(--gio-text)" }}>No duplicates found</div>
              <div className="text-[12px] mt-1" style={{ color: "var(--gio-muted-2)" }}>
                Your CRM is clean. Come back after new /form submissions.
              </div>
            </div>
          )}
          {!loading && groups.map((g, i) => {
            const sel = selections[i] || { targetId: g.suggested_target_id, checkedSources: new Set() };
            return (
              <div key={i} className="rounded-lg border p-3" data-testid={`merge-group-${i}`}
                style={{ borderColor: "var(--gio-border)", background: "var(--gio-surface)" }}>
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div className="inline-flex items-center gap-2">
                    <AlertCircle size={14} style={{ color: "var(--gio-secondary)" }} />
                    <span className="text-[12px] font-bold uppercase tracking-wider" style={{ color: "var(--gio-text)" }}>
                      Group {i + 1} · {g.members.length} likely duplicates
                    </span>
                  </div>
                  <button onClick={() => mergeGroup(i)} disabled={merging === i}
                    data-testid={`merge-confirm-${i}`}
                    className="btn-primary text-[11px] inline-flex items-center gap-1.5 disabled:opacity-50">
                    <GitMerge size={12} />
                    {merging === i ? "Merging…" : `Merge ${sel.checkedSources.size} into target`}
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {g.members.map(m => {
                    const isTarget = m.id === sel.targetId;
                    const isChecked = sel.checkedSources.has(m.id);
                    return (
                      <div key={m.id}
                        data-testid={`merge-member-${m.id}`}
                        className="rounded-lg border p-3 flex items-start gap-3"
                        style={{
                          borderColor: isTarget ? "var(--gio-primary)" : "var(--gio-border)",
                          background: isTarget ? "var(--gio-primary-soft)" : "transparent",
                        }}>
                        <div className="flex flex-col items-center gap-1 pt-1">
                          <label className="text-[9px] uppercase font-bold cursor-pointer" style={{ color: "var(--gio-primary)" }}>
                            <input type="radio"
                              checked={isTarget}
                              onChange={() => setTarget(i, m.id)}
                              data-testid={`merge-target-${m.id}`}
                              className="mr-1" />
                            Target
                          </label>
                          {!isTarget && (
                            <label className="text-[9px] uppercase font-bold cursor-pointer" style={{ color: "var(--gio-muted-2)" }}>
                              <input type="checkbox"
                                checked={isChecked}
                                onChange={() => toggleSource(i, m.id)}
                                data-testid={`merge-source-${m.id}`}
                                className="mr-1" />
                              Merge
                            </label>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-1">
                            {isTarget && <Crown size={11} style={{ color: "var(--gio-primary)" }} />}
                            <span className="font-bold text-[13px] truncate" style={{ color: "var(--gio-text)" }}>
                              {m.stage_name || m.legal_name || "Unknown"}
                            </span>
                            <span className="text-[9px] font-mono-ui px-1.5 py-0.5 rounded"
                              style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted-2)" }}>
                              richness {m.richness}
                            </span>
                          </div>
                          <div className="text-[10px] space-y-0.5" style={{ color: "var(--gio-muted-2)" }}>
                            {m.email && <div>📧 {m.email}</div>}
                            {m.phone && <div>📱 {m.phone}</div>}
                            {m.twitter && <div>🕊 @{m.twitter}</div>}
                            {m.instagram && <div>📸 @{m.instagram}</div>}
                            {m.onlyfans && <div>🔞 {m.onlyfans}</div>}
                            {(m.city || m.country) && <div>📍 {[m.city, m.country].filter(Boolean).join(", ")}</div>}
                            {m.source && <div>➜ Source: {m.source}</div>}
                            {m.tags?.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                {m.tags.slice(0, 5).map(t => (
                                  <span key={t} className="text-[9px] px-1.5 py-0.5 rounded"
                                    style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted-2)" }}>
                                    {t}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
            </>
          )}

          {tab === "manual" && (
            <div className="space-y-3" data-testid="merge-manual-body">
              {/* Search */}
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--gio-muted-3)" }} />
                <input value={manualSearch} onChange={(e) => setManualSearch(e.target.value)}
                  data-testid="merge-manual-search"
                  placeholder="Search name / email / handle / city…"
                  className="gio-input w-full pl-9" />
              </div>

              {/* Selection summary + action */}
              <div className="rounded-lg border p-3 flex items-center justify-between gap-3 flex-wrap sticky top-0 z-10"
                style={{ borderColor: manualChecked.size >= 2 ? "var(--gio-primary)" : "var(--gio-border)", background: "var(--gio-surface)" }}
                data-testid="merge-manual-summary">
                <div className="text-[12px]" style={{ color: "var(--gio-text)" }}>
                  <b>{manualChecked.size}</b> selected
                  {manualTargetId && (
                    <span className="ml-2" style={{ color: "var(--gio-muted-2)" }}>
                      · Target: <span style={{ color: "var(--gio-primary)" }}>
                        {(() => {
                          const t = allContacts.find(c => c.id === manualTargetId);
                          return t?.stage_name || t?.legal_name || "?";
                        })()}
                      </span>
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {manualChecked.size > 0 && (
                    <button onClick={() => { setManualChecked(new Set()); setManualTargetId(null); }}
                      data-testid="merge-manual-clear"
                      className="text-[11px] text-zinc-400 hover:text-red-400 font-mono-ui uppercase tracking-wider">
                      Clear
                    </button>
                  )}
                  <button onClick={runManualMerge}
                    disabled={manualChecked.size < 2 || !manualTargetId || manualMerging}
                    data-testid="merge-manual-run"
                    className="btn-primary text-[11px] inline-flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed">
                    <GitMerge size={12} />
                    {manualMerging ? "Merging…" : `Merge ${Math.max(0, manualChecked.size - 1)} into target`}
                  </button>
                </div>
              </div>

              {/* Contact list */}
              {manualLoading && (
                <div className="text-center py-8 text-[12px]" style={{ color: "var(--gio-muted-3)" }}>
                  Loading contacts…
                </div>
              )}
              {!manualLoading && (
                <div className="space-y-1.5" data-testid="merge-manual-list">
                  {filteredContacts.map(c => {
                    const isChecked = manualChecked.has(c.id);
                    const isTarget = manualTargetId === c.id;
                    return (
                      <div key={c.id} data-testid={`merge-manual-row-${c.id}`}
                        className="flex items-center gap-3 rounded-lg border p-2.5 transition"
                        style={{
                          borderColor: isTarget ? "var(--gio-primary)" : "var(--gio-border)",
                          background: isTarget ? "var(--gio-primary-soft)" : (isChecked ? "var(--gio-surface)" : "transparent"),
                        }}>
                        <input type="checkbox" checked={isChecked}
                          onChange={() => toggleManualCheck(c.id)}
                          data-testid={`merge-manual-check-${c.id}`}
                          className="w-4 h-4 shrink-0" />
                        {isChecked && (
                          <button
                            onClick={() => setManualTargetId(c.id)}
                            data-testid={`merge-manual-target-${c.id}`}
                            className="text-[9px] uppercase font-bold px-2 py-0.5 rounded border shrink-0"
                            style={{
                              borderColor: isTarget ? "var(--gio-primary)" : "var(--gio-border)",
                              background: isTarget ? "var(--gio-primary)" : "transparent",
                              color: isTarget ? "#000" : "var(--gio-muted-2)",
                            }}>
                            {isTarget ? <><Crown size={9} className="inline mr-0.5" />Target</> : "Set target"}
                          </button>
                        )}
                        {c.photos?.[0] ? (
                          <img src={c.photos[0]} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                        ) : (
                          <div className="w-8 h-8 rounded-full shrink-0 flex items-center justify-center text-[11px] font-bold"
                            style={{ background: "var(--gio-surface-hi)", color: "var(--gio-primary)" }}>
                            {((c.stage_name || c.legal_name || "?")[0] || "?").toUpperCase()}
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="font-bold text-[12px] truncate" style={{ color: "var(--gio-text)" }}>
                            {c.stage_name || c.legal_name || "Unknown"}
                          </div>
                          <div className="text-[10px] truncate" style={{ color: "var(--gio-muted-3)" }}>
                            {[c.email, c.twitter && `@${c.twitter}`, c.instagram && `@${c.instagram}`, c.city].filter(Boolean).join(" · ") || "—"}
                          </div>
                        </div>
                        {c.type && (
                          <span className="text-[9px] font-mono-ui uppercase px-1.5 py-0.5 rounded shrink-0"
                            style={{ background: "var(--gio-surface-hi)", color: "var(--gio-muted-2)" }}>
                            {c.type}
                          </span>
                        )}
                      </div>
                    );
                  })}
                  {filteredContacts.length === 0 && (
                    <div className="text-center py-6 text-[12px]" style={{ color: "var(--gio-muted-3)" }}>
                      No contacts match your search.
                    </div>
                  )}
                  {allContacts.length > filteredContacts.length && !manualSearch && (
                    <div className="text-center py-3 text-[10px]" style={{ color: "var(--gio-muted-3)" }}>
                      Showing 300 of {allContacts.length} — use search to narrow.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t text-[11px] text-center" style={{ borderColor: "var(--gio-border)", color: "var(--gio-muted-3)" }}>
          Tip: The target keeps its filled fields; sources fill in the blanks. All videos/pictures/posts/submissions are rewritten to point to the target.
        </div>
      </div>
    </div>
  );
}
